import React, { useState, useEffect } from 'react';
import Header from '../components/Header.jsx';
import api from '../services/api';
import '../styles/AdminGallery.css';

const AdminGallery = () => {
  const [galleryImages, setGalleryImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [formData, setFormData] = useState({
    image_url: '',
    title: '',
    description: '',
    is_visible: false,
    display_order: 0
  });

  useEffect(() => {
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    try {
      const response = await api.get('/admin/gallery');
      setGalleryImages(response.data.gallery || []);
    } catch (error) {
      console.error('Error fetching gallery:', error);
      alert('Failed to load gallery');
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, image_url: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.image_url) {
      alert('Please upload an image');
      return;
    }

    setLoading(true);
    try {
      await api.post('/admin/gallery', formData);
      alert('Gallery image uploaded successfully!');
      setFormData({ image_url: '', title: '', description: '', is_visible: false, display_order: 0 });
      setShowUploadForm(false);
      fetchGallery();
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload image');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (image) => {
    setEditingImage(image);
    setFormData({
      image_url: image.image_url,
      title: image.title || '',
      description: image.description || '',
      is_visible: image.is_visible === 1,
      display_order: image.display_order || 0
    });
    setShowEditModal(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.put(`/admin/gallery/${editingImage.gallery_id}`, formData);
      alert('Gallery image updated successfully!');
      setShowEditModal(false);
      setEditingImage(null);
      setFormData({ image_url: '', title: '', description: '', is_visible: false, display_order: 0 });
      fetchGallery();
    } catch (error) {
      console.error('Update error:', error);
      alert('Failed to update image');
    } finally {
      setLoading(false);
    }
  };

  const toggleVisibility = async (image) => {
    try {
      await api.put(`/admin/gallery/${image.gallery_id}`, {
        ...image,
        is_visible: image.is_visible === 1 ? 0 : 1
      });
      fetchGallery();
    } catch (error) {
      console.error('Toggle visibility error:', error);
      alert('Failed to update visibility');
    }
  };

  const handleDelete = async (imageId) => {
    if (window.confirm('Are you sure you want to delete this gallery image?')) {
      try {
        await api.delete(`/admin/gallery/${imageId}`);
        alert('Gallery image deleted successfully');
        fetchGallery();
      } catch (error) {
        console.error('Delete error:', error);
        alert('Failed to delete image');
      }
    }
  };

  return (
    <>
      <Header />
      <div className="admin-gallery-container">
        <div className="admin-gallery-header">
          <h1>Gallery Management</h1>
          <button 
            className="btn-primary" 
            onClick={() => setShowUploadForm(!showUploadForm)}
          >
            {showUploadForm ? 'Cancel' : '+ Upload New Image'}
          </button>
        </div>

        {showUploadForm && (
          <div className="upload-form-card">
            <h2>Upload Gallery Image</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Upload Image File:</label>
                <input 
                  type="file" 
                  accept="image/*" 
                  onChange={handleFileUpload}
                  className="form-control"
                />
              </div>

              <div className="form-group">
                <label>Or Paste Base64 URL:</label>
                <textarea
                  placeholder="data:image/jpeg;base64,..."
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  className="form-control"
                  rows="4"
                />
              </div>

              {formData.image_url && (
                <div className="image-preview">
                  <img src={formData.image_url} alt="Preview" />
                </div>
              )}

              <div className="form-group">
                <label>Title:</label>
                <input
                  type="text"
                  placeholder="Gallery image title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="form-control"
                />
              </div>

              <div className="form-group">
                <label>Description:</label>
                <textarea
                  placeholder="Image description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="form-control"
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label>Display Order (0 = first):</label>
                <input
                  type="number"
                  value={formData.display_order}
                  onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })}
                  className="form-control"
                />
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.is_visible}
                    onChange={(e) => setFormData({ ...formData, is_visible: e.target.checked })}
                  />
                  <span>Show in public gallery</span>
                </label>
              </div>

              <button type="submit" className="btn-primary" disabled={loading}>
                {loading ? 'Uploading...' : 'Upload Image'}
              </button>
            </form>
          </div>
        )}

        <div className="gallery-grid">
          {galleryImages.length === 0 ? (
            <p className="no-images">No gallery images yet. Upload your first image!</p>
          ) : (
            galleryImages.map((image) => (
              <div key={image.gallery_id} className="gallery-card">
                <div className="gallery-image">
                  <img src={image.image_url} alt={image.title || 'Gallery image'} />
                  <div className={`visibility-badge ${image.is_visible === 1 ? 'visible' : 'hidden'}`}>
                    {image.is_visible === 1 ? '👁️ Visible' : '🚫 Hidden'}
                  </div>
                </div>
                <div className="gallery-info">
                  <h3>{image.title || 'Untitled'}</h3>
                  {image.description && <p>{image.description}</p>}
                  <div className="gallery-meta">
                    <span className="order-badge">Order: {image.display_order}</span>
                  </div>
                </div>
                <div className="gallery-actions">
                  <button 
                    className="btn-toggle" 
                    onClick={() => toggleVisibility(image)}
                    title={image.is_visible === 1 ? 'Hide from gallery' : 'Show in gallery'}
                  >
                    {image.is_visible === 1 ? '👁️ Hide' : '👁️ Show'}
                  </button>
                  <button 
                    className="btn-edit" 
                    onClick={() => handleEdit(image)}
                  >
                    ✏️ Edit
                  </button>
                  <button 
                    className="btn-delete" 
                    onClick={() => handleDelete(image.gallery_id)}
                  >
                    🗑️ Delete
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Edit Modal */}
        {showEditModal && (
          <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h2>Edit Gallery Image</h2>
              <form onSubmit={handleUpdate}>
                <div className="form-group">
                  <label>Image Preview:</label>
                  <div className="image-preview">
                    <img src={formData.image_url} alt="Preview" />
                  </div>
                </div>

                <div className="form-group">
                  <label>Update Image (optional):</label>
                  <input 
                    type="file" 
                    accept="image/*" 
                    onChange={handleFileUpload}
                    className="form-control"
                  />
                </div>

                <div className="form-group">
                  <label>Title:</label>
                  <input
                    type="text"
                    placeholder="Gallery image title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="form-control"
                  />
                </div>

                <div className="form-group">
                  <label>Description:</label>
                  <textarea
                    placeholder="Image description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="form-control"
                    rows="3"
                  />
                </div>

                <div className="form-group">
                  <label>Display Order:</label>
                  <input
                    type="number"
                    value={formData.display_order}
                    onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })}
                    className="form-control"
                  />
                </div>

                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.is_visible}
                      onChange={(e) => setFormData({ ...formData, is_visible: e.target.checked })}
                    />
                    <span>Show in public gallery</span>
                  </label>
                </div>

                <div className="modal-actions">
                  <button type="submit" className="btn-primary" disabled={loading}>
                    {loading ? 'Updating...' : 'Update Image'}
                  </button>
                  <button 
                    type="button" 
                    className="btn-secondary" 
                    onClick={() => setShowEditModal(false)}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AdminGallery;